import React, { useEffect, useRef, useState, useCallback } from 'react';
import { set, ref, get } from 'firebase/database';
import { auth, db } from '../../firebase';
import { styled } from '@mui/system';
import { Table, TableBody, TableCell, TableContainer, TableRow, Paper, Typography, Radio, LinearProgress, TableHead, Tooltip } from '@mui/material';
import { useNavigate, useLocation } from "react-router-dom";
import { activitiesDATA } from '../Activity/AddActivity';
import NavBar from '../NavBar/NavBar';
import { getUserDetails, getCollegeName } from '../HomePage/userManagement';
import EmailAvatar from '../HomePage/EmailAvatar';

const MainContainer = styled('div')({
    height: "100vh",
    display: 'flex',
});
const FirstHalf = styled('div')({
    width: '20%',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    borderRight: '2.5px solid #D8D0D0',
    borderBottom: '2.5px solid #D8D0D0',
    background: '#fafafa',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
});
const ContentHalf = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'flex-start',
    padding: '15px',
    width: '78vw',
    background: 'white',
    maxHeight: '90%',
    overflow: 'auto'
});
const GenerateButton = styled('button')({
    marginTop: '20px',
    backgroundColor: '#2560CD',
    padding: '10px 26px',
    color: 'white',
    cursor: 'pointer',
    borderRadius: '10px',
    fontFamily: 'Poppins, sans-serif',
    fontWeight: 500,
    fontSize: '16px',
    borderColor: 'white ',
    "&:hover": {
        background: 'GrayText',
        opacity: '.7',
    }
});

const ratingLabels = [
    "Very Well",
    "Quite Well",
    "Moderately well",
    "Not So Well",
    "Not at all well",
    "NA"
];

const ratingMeanings = {
    "Very Well": "High Quality with No issues",
    "Quite Well": "Managed effectively with minor issues",
    "Moderately well": "Average effort with a few issues",
    "Not So Well": "Struggled with multiple challenges",
    "Not at all well": "Poor execution with major issues",
    "NA": "This skill was not required or relevant",
};

const questions = [
    { key: 'Project Management', text: 'How effectively did you plan, schedule, follow-up to complete tasks by meeting deadlines in the activities you have participated? Please mark NA if not applicable.' },
    { key: 'Design and Visualization', text: 'How well did you design - visuals, products, systems or creative materials for the activities you participated in? Please mark NA if not applicable.' },
    { key: 'Negotiations and Deal Making', text: 'How effectively did you negotiate, persuade or use deal making skills to convert leads, achieve targets or secure/ manage contracts? Please mark NA if not applicable.' },
    { key: 'Marketing and Promotions', text: 'How effectively did you apply marketing and promotion skills (e.g., branding, messaging, audience engagement) to promote products, services or programs to create awareness, attract users during these activities? Please mark NA if not applicable.' },
    { key: 'Critical Thinking and Problem Solving', text: 'To what extent did you apply critical thinking and problem-solving by analyzing information, evaluating, questioning, and drawing logical conclusions during these activities? Please mark NA if not applicable.' },
    { key: 'Cross Cultural Engagements', text: 'How well did you apply cultural awareness and multilingual skills to interact with people from diverse backgrounds in these activities? Please mark NA if not applicable.' },
    { key: 'External Interactions', text: 'How effectively did you communicate and collaborate with individuals outside of your team or external to the institution/ organization to complete tasks, gather information or achieve goals? Please mark NA if not applicable.' },
    { key: 'Internal Interactions', text: 'How well did you collaborate with your peers—within your team or organization—by sharing information, adapting to changes if any, and making decisions while maintaining mutual respect?  Please mark NA if not applicable.' },
    { key: 'Numerical and Quantitative Aptitude', text: 'How proficiently did you handle numerical data, perform mathematical calculations, or apply quantitative methods to interpret and make sense of numbers during these activities? Please mark NA if not applicable.' },
    { key: 'Resource Management', text: 'How well did you plan, allocate and manage time, money, personnel or other resources during these activities? Please mark NA if not applicable.' },
    { key: 'Analytics, Research and Insights', text: 'How effectively did you analyze data and evaluate information, conduct studies such as market research, manage large data sets, develop data models, or generate actionable insights while carrying out these activities? Please mark NA if not applicable.' },
    { key: 'Creation and Innovation', text: 'To what extent were you able to explore fresh approaches and solutions, generate innovative concepts,  or ideas and apply them to create new and innovative products (physical or software) or services during these activities? Please mark NA if not applicable.' },
    { key: 'Subject Matter Expertise', text: 'How effectively have you developed deep expertise in your domain or subject matter, and further applied this knowledge to guide, advise, consult, train, or mentor others in a professional or educational setting? Please mark NA if not applicable.' },
    { key: 'Operations and Process Excellence', text: 'How effectively did you manage operations, including process improvement, vendor management, and/or procurement during any of the activities as stated below ? Please mark NA if not applicable.' },
    { key: 'Recruitment and Support', text: 'How well did you select or onboard new team members—help them adapt and identify their need for any training to bring them upto speed to contribute to projects or activities? Please mark NA if not applicable.' },
    { key: 'Administration', text: 'How effectively did you handle administrative tasks, such as documentation, correspondence, office management, reporting, and compliance? Please mark NA if not applicable.' },
    { key: 'Self Management and Development', text: 'To what extent did you demonstrate a positive attitude, remain proactive, pursue continuous learning, stay motivated to work efficiently, and adapt to challenges? Please mark "N/A" if not applicable.' },
    { key: 'Dynamic Presence', text: 'How effectively did you demonstrate confidence, presence, and audience engagement in high-visibility settings such as public speaking, executive interactions, or performance-based activities (e.g., sports, drama, dance, music)? Please mark NA if not applicable.' },
    { key: 'Social Initiatives and Impact', text: 'How well did you contribute to social initiatives and create measurable impact during your participation in the following initiatives? Consider aspects such as supporting marginalized communities, promoting equity, environmental sustainability, or advocating for diversity and inclusion. Please mark NA if not applicable.' },
    { key: 'Monitoring, Inspecting and Testing', text: 'How effectively did you track key metrics, review processes, conduct tests (e.g., A/B testing), and analyze performance trends to ensure quality, compliance, or data-driven decisions. Please mark NA if not applicable.' },
    { key: 'Leadership', text: 'To what extent did you demonstrate leadership in these activities by taking initiatives, setting a clear vision, guiding and motivating your team, and making decisions?  Please mark NA if not applicable.' },
];

const dateStamp = new Date().toString();

const QuestionairePart2 = () => {
    const navigate = useNavigate();
    const contentRef = useRef(null);
    const location = useLocation();
    const { selectedKeys } = location.state || {};
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [errorMessage, setErrorMessage] = useState("");
    const [user, setUser] = useState(null);
    const [navigationDirection, setNavigationDirection] = useState('forward');
    const [isAutoNavigate, setIsAutoNavigate] = useState(true);
    const [selectedQuestionsWithHandled, setSelectedQuestionsWithHandled] = useState({});
    const [totalWeight, setTotalWeight] = useState(0);
    const [isManualNavigation, setIsManualNavigation] = useState(false);

    useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged((authUser) => {
            if (authUser) {
                setUser(authUser);
                fetchAndInitialize();
            } else {
                setUser(null);
            }
        });

        return unsubscribe;
    }, []);

    useEffect(() => {
        const fetchAndInitialize = async () => {
            if (!user) return;

            try {
                const userUid = user.uid;
                const activityDetailsRef = ref(db, `${getCollegeName()}/Activities/${userUid}`);
                const snapshot = await get(activityDetailsRef);
                const activityData = snapshot.val();

                if (activityData) {
                    const transformedActivities = transformActivities(activityData, userUid);
                    const initialState = initializeQuestionsWithActivities(selectedKeys, questions, transformedActivities);
                    setSelectedQuestionsWithHandled(initialState);
                    const weight = calculateTotalWeight(transformedActivities);
                    setTotalWeight(weight);
                }
            } catch (error) {
                console.error('Error fetching and initializing activity data:', error);
            }
        };

        fetchAndInitialize();
    }, [user, selectedKeys, questions]);

    const calculateTotalWeight = (activities) => {
        return activities.reduce((sum, activity) => sum + activity.weight, 0);
    };

    useEffect(() => {
        if (contentRef.current) {
            contentRef.current.scrollTop = 0;
        }
    }, [currentQuestionIndex]);

    const fetchAndInitialize = async () => {
        if (!user) return;

        try {
            const userUid = user.uid;
            const activityDetailsRef = ref(db, `${getCollegeName()}/Activities/${userUid}`);
            const snapshot = await get(activityDetailsRef);
            const activityData = snapshot.val();

            if (activityData) {
                const transformedActivities = transformActivities(activityData, userUid);
                const initialState = initializeQuestionsWithActivities(selectedKeys, questions, transformedActivities);
                setSelectedQuestionsWithHandled(initialState);
            }
        } catch (error) {
            console.error('Error fetching and initializing activity data:', error);
        }
    };

    const transformActivities = (dbData, userUid) => {
        const userActivities = dbData || {};
        return Object.values(userActivities).map((activity) => ({
            activityName: activity.activity,
            highestLevel: activity.highestLevel,
            outcome: activity.outcome,
            enjoyment: activity.enjoyment,
            weight: activity.weights,
        }));
    };

    useEffect(() => {
        if (selectedKeys) {
            const filteredQuestions = questions.filter((question) =>
                selectedKeys.includes(question.key)
            ).map((question) => ({
                key: question.key,
                text: question.text,
                activities: activitiesDATA?.map((activity) => ({
                    activityName: activity,
                    handled: ''
                })),
            }));

            setSelectedQuestionsWithHandled(filteredQuestions);
        }
    }, [selectedKeys]);

    const initializeQuestionsWithActivities = (selectedKeys, questions, activities) => {
        return questions
            .filter((q) => selectedKeys.includes(q.key))
            .reduce((acc, question) => {
                acc[question.key] = {
                    question: question.text,
                    activities: activities.map((activity) => ({
                        activityName: activity.activityName,
                        handled: '',
                        weight: activity.weight,
                    })),
                    totalScore: 0,
                    score: 0
                };
                return acc;
            }, {});
    };

    const totalQuestions = Object.keys(selectedQuestionsWithHandled).length;
    const answeredQuestions = Object.values(selectedQuestionsWithHandled).filter(
        question => question.activities.every(activity => activity.handled !== '')
    ).length;
    const completedProgress = (answeredQuestions / totalQuestions) * 100;

    const handleActivityUpdate = (key, activityIndex, value) => {
        setSelectedQuestionsWithHandled((prev) => {
            const updatedActivities = [...prev[key].activities];
            updatedActivities[activityIndex] = {
                ...updatedActivities[activityIndex],
                handled: value,
            };
            const totalScore = updatedActivities.reduce(
                (sum, activity) => sum + activity.handled * activity.weight,
                0
            );

            const score = totalWeight > 0 ? totalScore / totalWeight : 0;

            const updatedQuestion = {
                ...prev[key],
                activities: updatedActivities,
                totalScore,
                score,
            };
            const isQuestionComplete = updatedActivities.every(activity => activity.handled !== '');

            if (isQuestionComplete && currentQuestionIndex === Object.keys(selectedQuestionsWithHandled).indexOf(key)) {
                setIsAutoNavigate(true);
            }

            return {
                ...prev,
                [key]: updatedQuestion,
            };
        });
    };

    const saveResponsesToFirebase = async () => {
        if (user) {
            try {
                const userUid = user.uid;
                const responsesRef2 = ref(db, `${getCollegeName()}/questionnaire/${userUid}/response${dateStamp}`);
                await set(responsesRef2, selectedQuestionsWithHandled);
            } catch (error) {
                console.error("Error saving responses to the database:", error);
            }
        } else {
            console.error("No user is authenticated.");
        }
    };

    const handleNextButtonClick = () => {
        const currentQuestion = selectedQuestionsWithHandled[Object.keys(selectedQuestionsWithHandled)[currentQuestionIndex]];

        if (currentQuestion && currentQuestion.activities) {
            const isCurrentQuestionAnswered = currentQuestion.activities.every(activity => activity.handled !== '');

            if (isCurrentQuestionAnswered) {
                setErrorMessage("");
                setIsManualNavigation(false);
                goToNextQuestion();
            } else {
                setErrorMessage("Please answer everything before moving to the next question.");
                setTimeout(() => {
                    setErrorMessage(null);
                }, 3000);
            }
        } else {
            setErrorMessage("Unable to fetch activities for the current question.");
        }
    };

    const goToNextQuestion = useCallback(() => {
        const keys = Object.keys(selectedQuestionsWithHandled);
        if (currentQuestionIndex < keys.length - 1) {
            setNavigationDirection('forward');
            setCurrentQuestionIndex(currentQuestionIndex + 1);
        }
    }, [currentQuestionIndex, selectedQuestionsWithHandled]);

    const goToPreviousQuestion = () => {
        if (currentQuestionIndex > 0) {
            setIsManualNavigation(true);
            setNavigationDirection('backward');
            setCurrentQuestionIndex(currentQuestionIndex - 1);
        } else {
            navigate('/quetionairepart1');
        }
    };


    useEffect(() => {
        const currentQuestion = selectedQuestionsWithHandled[Object.keys(selectedQuestionsWithHandled)[currentQuestionIndex]];

        if (
            !isManualNavigation &&
            isAutoNavigate &&
            navigationDirection === 'forward' &&
            currentQuestion &&
            currentQuestion.activities.every(activity => activity.handled !== '') &&
            currentQuestionIndex < Object.keys(selectedQuestionsWithHandled).length - 1
        ) {
            setTimeout(() => {
                goToNextQuestion();
            }, 500);
        }

        if (!isAutoNavigate) {
            setIsAutoNavigate(true);
        }

        if (isManualNavigation) {
            setIsManualNavigation(false);
        }
    }, [selectedQuestionsWithHandled, currentQuestionIndex, navigationDirection, isAutoNavigate, goToNextQuestion, isManualNavigation]);

    const handleFinalOutputClick = () => {
        saveResponsesToFirebase();
        navigate('/finaloutput', {
            state: {
                selectedKeys,
                selectedQuestionsWithHandled,
                totalWeight
            },
        });
    };

    return (
        <MainContainer>
            <FirstHalf>
                <NavBar activePage={1} user={getUserDetails} />
            </FirstHalf>
            <ContentHalf ref={contentRef}>
                <div style={{ display: "flex", alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                    <Typography style={{ fontFamily: 'Poppins, sans-serif', fontSize: '20px', fontWeight: 600, marginLeft: 5, marginBottom: 5 }}>
                        Self reflection of your performance and expertise
                    </Typography>
                    <EmailAvatar />
                </div>
                <div style={{ width: '100%' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 30 }}>
                        <Typography style={{
                            fontFamily: 'Poppins, sans-serif',
                            fontSize: 14,
                            fontWeight: 500,
                        }}>
                            Question: {currentQuestionIndex + 1}/{totalQuestions}
                        </Typography>
                    </div>
                    <LinearProgress variant="determinate" value={completedProgress} color='inherit' style={{ marginBottom: '5px', height: '10px', borderRadius: "16px " }} />
                </div>
                {Object.entries(selectedQuestionsWithHandled).map(([key, question], index) => {
                    if (index === currentQuestionIndex) {
                        return (
                            <div key={key} style={{ width: '100%' }}>
                                <Typography
                                    style={{
                                        padding: '10px',
                                        border: '1px solid black',
                                        background: '#2560CD',
                                        color: 'white',
                                        fontFamily: 'Poppins, sans-serif',
                                        fontWeight: 500,
                                        fontSize: '14px',
                                        width: '98%',
                                    }}
                                >
                                    {index + 1}. {question.question}
                                </Typography>

                                <TableContainer component={Paper} sx={{ width: '100%' }}>
                                    <Table>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell
                                                    sx={{
                                                        fontWeight: "bold",
                                                        fontSize: 14,
                                                        padding: '4px',
                                                        whiteSpace: "normal",
                                                        wordWrap: "break-word",
                                                    }}
                                                >
                                                    Engagements:
                                                </TableCell>
                                                {/* Table headers for rating */}
                                                {[
                                                    "Very Well (With No issues)",
                                                    "Quite Well (With Minor issues)",
                                                    "Moderately well (With a few issues)",
                                                    "Not So Well (With many issues)",
                                                    "Not at all well (With major issues)",
                                                    "NA",
                                                ].map((header, index) => (
                                                    <TableCell
                                                        key={index}
                                                        sx={{
                                                            fontWeight: "bold",
                                                            fontSize: 14,
                                                            padding: '4px',
                                                            width: 140,
                                                            textAlign: "center",
                                                            whiteSpace: "normal",
                                                            wordWrap: "break-word",
                                                        }}
                                                        align="center"
                                                    >
                                                        {header.includes('(') ? (
                                                            <>
                                                                {header.substring(0, header.indexOf('(')).trim()}
                                                                <br />
                                                                {header.substring(header.indexOf('(')).trim()}
                                                            </>
                                                        ) : (
                                                            header
                                                        )}
                                                    </TableCell>
                                                ))}
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {question.activities &&
                                                question.activities.map((activityData, activityIndex) => (
                                                    <TableRow key={activityIndex}>
                                                        <TableCell sx={{ padding: "4px", textAlign: "left" }}>
                                                            {activityData.activityName}
                                                        </TableCell>
                                                        {[5, 4, 3, 2, 1, 0].map((value, columnIndex) => (
                                                            <TableCell align="center" key={columnIndex}>
                                                                <Tooltip title={ratingMeanings[ratingLabels[columnIndex]]} arrow>
                                                                    <Radio
                                                                        checked={String(activityData.handled) === String(value)}
                                                                        onChange={() => handleActivityUpdate(key, activityIndex, value)}
                                                                        value={value}
                                                                    />
                                                                </Tooltip>
                                                            </TableCell>
                                                        ))}
                                                    </TableRow>
                                                ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                                {errorMessage && (
                                    <Typography variant="body1" color="error" style={{
                                        marginTop: '10px', fontFamily: 'Poppins, sans-serif',
                                        fontWeight: 500,
                                        fontSize: '14px'
                                    }}>
                                        {errorMessage}
                                    </Typography>
                                )}

                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <GenerateButton onClick={goToPreviousQuestion}>
                                        Previous
                                    </GenerateButton>
                                    <GenerateButton
                                        onClick={
                                            answeredQuestions === totalQuestions
                                                ? handleFinalOutputClick
                                                : handleNextButtonClick
                                        }
                                    >
                                        {answeredQuestions === totalQuestions
                                            ? "View Results"
                                            : "Next"
                                        }
                                    </GenerateButton>
                                </div>

                            </div>
                        );
                    }
                    return null;
                })}
            </ContentHalf>
        </MainContainer>
    );
};

export default QuestionairePart2;
